import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Upload, Download, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { type: 'ID Proof', description: 'Aadhaar, Voter ID, etc.' },
  { type: 'Land Record', description: 'Land ownership documents' },
  { type: 'Bank Statement', description: 'Recent bank statements' },
  { type: 'Yield Certificate', description: 'Crop yield certificates' },
  { type: 'Insurance Policy', description: 'Crop insurance documents' },
  { type: 'Previous Loan Document', description: 'Previous loan records' },
  { type: 'Sale Receipt', description: 'Crop sale receipts' },
  { type: 'Purchase Invoice', description: 'Input purchase invoices' }
];

const Step5Documents = ({ farmerId, documents, onDocumentsUpdate }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (documentType, file) => {
    if (!farmerId) {
      toast.error('Please save the farmer profile first before uploading documents');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);

      await axios.post(`${API}/farmers/${farmerId}/documents`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${documentType} uploaded successfully!`);
      onDocumentsUpdate(); // Refresh documents and farmer data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download document');
    }
  };

  const hasDocument = (docType) => {
    return documents.some(doc => doc.document_type === docType);
  };

  const getDocument = (docType) => {
    return documents.find(doc => doc.document_type === docType);
  };

  if (!farmerId) {
    return (
      <div className="text-center py-12">
        <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Save Profile First</h3>
        <p className="text-gray-600">
          Please complete and save the farmer profile before uploading documents.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Upload Documents</h3>
        <p className="text-sm text-gray-600">
          Upload relevant documents to improve credit score assessment. All documents are optional but recommended.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {DOCUMENT_TYPES.map((docType) => (
            <div
              key={docType.type}
              className="border-2 border-dashed rounded-lg p-6 text-center"
              data-testid={`document-upload-${docType.type.toLowerCase().replace(/ /g, '-')}`}
            >
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <h3 className="font-semibold mb-2">{docType.type}</h3>
              {hasDocument(docType.type) ? (
                <div className="space-y-2">
                  <Badge className="bg-green-100 text-green-800">Uploaded ✓</Badge>
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const doc = getDocument(docType.type);
                        handleDownloadDocument(doc.id, doc.filename);
                      }}
                      data-testid={`download-${docType.type.toLowerCase().replace(/ /g, '-')}-button`}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-3">{docType.description}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploading}
                    onClick={() => document.getElementById(`upload-${docType.type.replace(/ /g, '-')}`).click()}
                    data-testid={`upload-${docType.type.toLowerCase().replace(/ /g, '-')}-button`}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </Button>
                  <input
                    id={`upload-${docType.type.replace(/ /g, '-')}`}
                    type="file"
                    className="hidden"
                    onChange={(e) => handleFileUpload(docType.type, e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Document Guidelines</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Accepted formats: PDF, JPG, JPEG, PNG</li>
          <li>• Maximum file size: 10 MB per document</li>
          <li>• Ensure documents are clear and readable</li>
          <li>• Documents are securely stored and encrypted</li>
        </ul>
      </div>
    </div>
  );
};

export default Step5Documents;
